import type { LocalizedText } from '../permissions.ts';
import { contrastRatio } from '../platform.ts';
import { mix, readableOn, type PaletteColors } from './colors.ts';

/**
 * Paletas do Estúdio de estilos (ADR 0128): as cores da tela, sem nada de estrutura. As cinco
 * primeiras são as dos estilos prontos do ADR 0126, com as mesmas cores, para quem já escolheu
 * um deles continuar igual.
 */
export interface PaletteSpec extends PaletteColors {
  id: string;
  name: LocalizedText;
  /** Cor de destaque que a paleta traz marcada; o cliente pode trocar. */
  accent: string;
}

/** As cores que o cliente pode ajustar por cima de uma paleta. */
export interface PaletteOverrides {
  paper?: string;
  sheet?: string;
  ink?: string;
  muted?: string;
  rule?: string;
  sidebar?: string;
  sidebarText?: string;
}

/** O resto da lateral (apoio, linha, passar o mouse) sai do fundo e do texto dela. */
function sidebarOf(bg: string, text: string): NonNullable<PaletteColors['sidebar']> {
  const lighten = contrastRatio('#ffffff', bg) > contrastRatio('#000000', bg);
  return {
    bg,
    text,
    muted: readableOn(mix(text, bg, 0.6), [bg], lighten),
    rule: mix(text, bg, 0.12),
    hover: mix(text, bg, 0.08),
  };
}

/** A base de uma paleta clara ou escura a partir das cinco cores da folha. */
function baseOf(c: {
  paper: string;
  sheet: string;
  ink: string;
  muted: string;
  rule: string;
}): NonNullable<PaletteColors['base']> {
  return { ...c, tonal: mix(c.ink, c.sheet, 0.05) };
}

const pal = (
  id: string,
  pt: string,
  en: string,
  accent: string,
  c: { paper: string; sheet: string; ink: string; muted: string; rule: string },
  side: [string, string],
  dark = false,
): PaletteSpec => ({
  id,
  name: { 'pt-BR': pt, en },
  accent,
  base: baseOf(c),
  sidebar: sidebarOf(side[0], side[1]),
  dark,
});

export const PALETTES: readonly PaletteSpec[] = [
  {
    id: 'beadev',
    name: { 'pt-BR': 'Beadev', en: 'Beadev' },
    accent: '#c4ef2f',
    base: null,
    sidebar: null,
    dark: false,
  },
  {
    id: 'clear',
    name: { 'pt-BR': 'Claro e leve', en: 'Light and airy' },
    accent: '#2563eb',
    base: {
      paper: '#f5f6f8',
      sheet: '#ffffff',
      ink: '#111827',
      muted: '#5b6472',
      rule: '#e3e6eb',
      tonal: '#eef0f3',
    },
    sidebar: {
      bg: '#ffffff',
      text: '#111827',
      muted: '#5b6472',
      rule: '#e3e6eb',
      hover: '#f1f3f6',
    },
    dark: false,
  },
  {
    id: 'top',
    name: { 'pt-BR': 'Zinco', en: 'Zinc' },
    accent: '#0f766e',
    base: {
      paper: '#f7f7f8',
      sheet: '#ffffff',
      ink: '#18181b',
      muted: '#5d5d66',
      rule: '#e4e4e7',
      tonal: '#f0f0f2',
    },
    sidebar: {
      bg: '#18181b',
      text: '#fafafa',
      muted: '#a1a1aa',
      rule: '#2c2c31',
      hover: '#27272a',
    },
    dark: false,
  },
  {
    id: 'warm',
    name: { 'pt-BR': 'Papel e terracota', en: 'Paper and terracotta' },
    accent: '#c2410c',
    base: {
      paper: '#f6f1e9',
      sheet: '#fffdf9',
      ink: '#2b211a',
      muted: '#6f6255',
      rule: '#e7ded1',
      tonal: '#efe8dd',
    },
    sidebar: {
      bg: '#2b211a',
      text: '#fffdf9',
      muted: '#c9bcac',
      rule: '#41352b',
      hover: '#3a2e25',
    },
    dark: false,
  },
  {
    id: 'dark',
    name: { 'pt-BR': 'Escuro grafite', en: 'Graphite dark' },
    accent: '#7c3aed',
    base: {
      paper: '#0f1115',
      sheet: '#181b22',
      ink: '#f4f5f7',
      muted: '#a0a6b2',
      rule: '#2a2f3a',
      tonal: '#20242d',
    },
    sidebar: {
      bg: '#0a0c10',
      text: '#f4f5f7',
      muted: '#8b93a3',
      rule: '#1f232c',
      hover: '#161920',
    },
    dark: true,
  },
  pal(
    'corp',
    'Azul corporativo',
    'Corporate blue',
    '#2563eb',
    { paper: '#f4f6f9', sheet: '#ffffff', ink: '#0f172a', muted: '#556074', rule: '#e2e7ee' },
    ['#0f1e3d', '#e8edf7'],
  ),
  pal(
    'petrol',
    'Verde-petróleo',
    'Petrol green',
    '#0f766e',
    { paper: '#f3f6f5', sheet: '#ffffff', ink: '#10201c', muted: '#54655f', rule: '#dfe7e4' },
    ['#0f2e2a', '#e6f0ed'],
  ),
  pal(
    'wine',
    'Vinho',
    'Wine',
    '#9f1239',
    { paper: '#f7f3f4', sheet: '#ffffff', ink: '#241418', muted: '#6b5a5f', rule: '#eadfe2' },
    ['#3b0d1c', '#f6e9ee'],
  ),
  pal(
    'mono',
    'Tinta',
    'Ink',
    '#18181b',
    { paper: '#fafafa', sheet: '#ffffff', ink: '#0a0a0a', muted: '#5c5c5c', rule: '#e5e5e5' },
    ['#fafafa', '#0a0a0a'],
  ),
  pal(
    'amber',
    'Oficina',
    'Workshop',
    '#b45309',
    { paper: '#eceef0', sheet: '#ffffff', ink: '#111417', muted: '#545b63', rule: '#d6dade' },
    ['#1f2a2e', '#e9eef0'],
  ),
  pal(
    'sky',
    'Céu',
    'Sky',
    '#0369a1',
    { paper: '#f2f7fb', sheet: '#ffffff', ink: '#0c1b2a', muted: '#51606f', rule: '#dde7f0' },
    ['#e6f0f8', '#0c1b2a'],
  ),
  pal(
    'rose',
    'Rosa suave',
    'Soft rose',
    '#be185d',
    { paper: '#fbf5f6', sheet: '#ffffff', ink: '#2a1a1f', muted: '#6d5a60', rule: '#efe1e4' },
    ['#ffffff', '#2a1a1f'],
  ),
  pal(
    'night',
    'Azul-noite',
    'Night blue',
    '#38bdf8',
    { paper: '#0b1220', sheet: '#111a2e', ink: '#e6ecf7', muted: '#93a0b8', rule: '#22304a' },
    ['#070d18', '#e6ecf7'],
    true,
  ),
];

export type PaletteId = string;

export function paletteSpec(id: string): PaletteSpec | undefined {
  return PALETTES.find((p) => p.id === id);
}

/** Força da cor para ser destaque: saturada e nem clara nem escura demais. */
function accentScore(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255) as [
    number,
    number,
    number,
  ];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const s = max === min ? 0 : (max - min) / (1 - Math.abs(2 * l - 1));
  return s * (1 - Math.abs(l - 0.5));
}

/**
 * A paleta montada com as cores do logo: a mais forte vira o destaque, uma cor bem escura do
 * logo (se houver) vira a lateral, e o resto da tela fica neutro, com um toque do destaque.
 * Sem cor nenhuma, null (quem chama fica na paleta escolhida).
 */
export function paletteFromLogo(colors: readonly string[]): PaletteSpec | null {
  if (!colors.length) return null;
  const accent = [...colors].sort((a, b) => accentScore(b) - accentScore(a))[0]!;
  const darkest = colors.find((c) => contrastRatio('#ffffff', c) > 12);
  const ink = mix(accent, '#121418', 0.08);
  const sidebar = darkest ?? mix(accent, '#0e1014', 0.18);
  return {
    id: 'logo',
    name: { 'pt-BR': 'Cores do logo', en: 'Logo colors' },
    accent,
    base: baseOf({
      paper: mix(accent, '#f6f7f8', 0.035),
      sheet: '#ffffff',
      ink,
      muted: readableOn(
        mix(accent, '#5d636d', 0.12),
        ['#ffffff', mix(accent, '#f6f7f8', 0.035)],
        false,
      ),
      rule: mix(accent, '#e3e5e9', 0.07),
    }),
    sidebar: sidebarOf(sidebar, '#f3f4f6'),
    dark: false,
  };
}

/** A paleta com os ajustes do cliente por cima (a lateral recalcula o apoio, a linha e o realce). */
export function withOverrides(spec: PaletteSpec, o: PaletteOverrides | undefined): PaletteSpec {
  if (!o || Object.keys(o).length === 0) return spec;
  const base = spec.base ?? {
    paper: '#f5f6f8',
    sheet: '#ffffff',
    ink: '#0b0e14',
    muted: '#5f6676',
    rule: '#e3e6eb',
    tonal: '#eceef2',
  };
  const side = spec.sidebar ?? sidebarOf('#0b0e14', '#fafbfc');
  const nextBase = baseOf({
    paper: o.paper ?? base.paper,
    sheet: o.sheet ?? base.sheet,
    ink: o.ink ?? base.ink,
    muted: o.muted ?? base.muted,
    rule: o.rule ?? base.rule,
  });
  const nextSide =
    o.sidebar || o.sidebarText ? sidebarOf(o.sidebar ?? side.bg, o.sidebarText ?? side.text) : side;
  return { ...spec, base: nextBase, sidebar: nextSide };
}
